/**
 * BasinFeed Component
 * 
 * Real-time community feed for a basin.
 * "Someone out here has my back, even if I've never met them."
 * 
 * Shows hazards, community tips, and activity with human-friendly messaging.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  IconButton,
  Chip,
  Button,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  Fade,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  NavigateNext as NavIcon,
  ThumbUp as ConfirmIcon,
  ThumbDown as DenyIcon,
  AccessTime as TimeIcon,
  CheckCircle as VerifiedIcon,
  Warning as HazardIcon,
  People as CommunityIcon,
} from '@mui/icons-material';
import { telegram } from '../services/telegram';

interface BasinHazard {
  id: string;
  type: string;
  typeLabel: string;
  typeEmoji: string;
  severity: string;
  latitude: number;
  longitude: number;
  note: string | null;
  contextNote: string | null;
  confirmations: number;
  yesVotes: number;
  noVotes: number;
  isGhost: boolean;
  timeAgo: string;
  createdAt: string;
}

interface BasinFeedData {
  basin: { id: string; name: string };
  pulse: {
    greeting: string;
    message: string;
    activeWorkers: number;
    safeReturnsToday: number;
    hazardCount: number;
  };
  hazards: BasinHazard[];
  timestamp: string;
}

interface BasinFeedProps {
  basin?: string;
  onNavigateToHazard?: (lat: number, lng: number) => void;
  maxHeight?: number | string;
}

export const BasinFeed: React.FC<BasinFeedProps> = ({
  basin = 'permian',
  onNavigateToHazard,
  maxHeight = 500,
}) => {
  const [feed, setFeed] = useState<BasinFeedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const lastFetchRef = useRef<number>(0);

  // Fetch feed data
  const fetchFeed = useCallback(async (showLoading = true) => {
    // Prevent rapid refetches
    const now = Date.now();
    if (now - lastFetchRef.current < 5000) return;
    lastFetchRef.current = now;

    if (showLoading) setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/basin/${basin}/feed`, {
        headers: {
          'X-Telegram-User-Id': telegram.getUserId() || '',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setFeed(data);
      } else {
        const errData = await response.json().catch(() => ({}));
        setError(errData.error || `Failed to load feed (${response.status})`);
      }
    } catch (error) {
      console.error('Error fetching basin feed:', error);
      setError('Network error - please check your connection');
    } finally {
      setLoading(false);
    }
  }, [basin]);

  // Initial fetch and polling
  useEffect(() => {
    fetchFeed();
    
    // Poll every 30 seconds
    const interval = setInterval(() => fetchFeed(false), 30000);
    return () => clearInterval(interval);
  }, [fetchFeed]);

  // Confirm or deny a hazard
  const handleConfirm = async (hazardId: string, confirmed: boolean) => {
    setConfirmingId(hazardId);
    
    try {
      await fetch('/api/hazards/confirm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Telegram-User-Id': telegram.getUserId() || '',
        },
        body: JSON.stringify({ hazardId, confirmed }),
      });
      
      // Optimistic update
      setFeed(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          hazards: prev.hazards.map(h => 
            h.id === hazardId 
              ? { 
                  ...h, 
                  confirmations: confirmed ? h.confirmations + 1 : h.confirmations,
                  yesVotes: confirmed ? h.yesVotes + 1 : h.yesVotes,
                  noVotes: !confirmed ? h.noVotes + 1 : h.noVotes,
                }
              : h
          ),
        };
      });
      
      // Refresh after a moment
      setTimeout(() => fetchFeed(false), 2000);
    } catch (error) {
      console.error('Error confirming hazard:', error);
    } finally {
      setConfirmingId(null);
    }
  };

  // Get severity color
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'urgent': return '#ef4444';
      case 'high': return '#f97316';
      case 'normal': return '#f59e0b';
      default: return '#64748b';
    }
  };

  if (loading && !feed) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} sx={{ color: '#60a5fa' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95))',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: 3,
          p: 3,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ color: '#ef4444', fontWeight: 600, fontSize: '0.95rem', mb: 1 }}>
          ⚠️ Unable to Load Basin Feed
        </Typography>
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem', mb: 2 }}>
          {error}
        </Typography>
        <Box 
          component="button"
          onClick={() => fetchFeed()}
          sx={{
            background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.2), rgba(96, 165, 250, 0.3))',
            border: '1px solid rgba(96, 165, 250, 0.5)',
            borderRadius: 2,
            px: 3,
            py: 1,
            color: '#60a5fa',
            cursor: 'pointer',
            '&:hover': { background: 'rgba(96, 165, 250, 0.3)' }
          }}
        >
          Try Again
        </Box>
      </Box>
    );
  }

  if (!feed) return null;

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95))',
        border: '1px solid rgba(96, 165, 250, 0.2)',
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid rgba(96, 165, 250, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
            {feed.basin.name} Feed
          </Typography>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem' }}>
            {feed.pulse.activeWorkers} active • {feed.hazards.length} hazard{feed.hazards.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        
        <IconButton 
          size="small" 
          onClick={() => fetchFeed()}
          disabled={loading}
        >
          <RefreshIcon 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.5)', 
              fontSize: 20,
              animation: loading ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }} 
          />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          borderBottom: '1px solid rgba(96, 165, 250, 0.1)',
          '& .MuiTab-root': {
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '0.8rem',
            minHeight: 40,
            '&.Mui-selected': { color: '#60a5fa' },
          },
          '& .MuiTabs-indicator': { backgroundColor: '#60a5fa' },
        }}
      >
        <Tab icon={<HazardIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Hazards" />
        <Tab icon={<CommunityIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Community" />
      </Tabs>

      {/* Content */}
      <Box sx={{ maxHeight, overflowY: 'auto' }}>
        {/* Hazards Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 1 }}>
            {feed.hazards.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.9rem' }}>
                  All clear in {feed.basin.name} 🎉
                </Typography>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '0.75rem', mt: 0.5 }}>
                  No active hazards reported
                </Typography>
              </Box>
            ) : (
              feed.hazards.map((hazard, idx) => (
                <Fade in key={hazard.id} timeout={300 + idx * 100}>
                  <Card
                    sx={{
                      mb: 1,
                      p: 1.5,
                      background: hazard.isGhost 
                        ? 'rgba(100, 116, 139, 0.1)'
                        : `rgba(${getSeverityColor(hazard.severity)}, 0.05)`,
                      border: `1px solid ${hazard.isGhost ? 'rgba(100, 116, 139, 0.3)' : getSeverityColor(hazard.severity)}33`,
                      borderRadius: 2,
                      opacity: hazard.isGhost ? 0.7 : 1,
                    }}
                  >
                    {/* Hazard Header */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '1.5rem' }}>
                          {hazard.isGhost ? '👻' : hazard.typeEmoji}
                        </Typography>
                        <Box>
                          <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.9rem' }}>
                            {hazard.typeLabel}
                            {hazard.isGhost && ' (may have moved)'}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TimeIcon sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: 12 }} />
                            <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.7rem' }}>
                              {hazard.timeAgo}
                            </Typography>
                            {hazard.confirmations > 0 && (
                              <>
                                <Typography sx={{ color: 'rgba(255, 255, 255, 0.3)', mx: 0.5 }}>•</Typography>
                                <VerifiedIcon sx={{ color: '#22c55e', fontSize: 12 }} />
                                <Typography sx={{ color: '#22c55e', fontSize: '0.7rem' }}>
                                  {hazard.confirmations} confirmed
                                </Typography>
                              </>
                            )}
                          </Box>
                        </Box>
                      </Box>
                      
                      {/* Severity badge */}
                      {hazard.severity !== 'normal' && (
                        <Chip
                          label={hazard.severity}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            background: getSeverityColor(hazard.severity) + '22',
                            color: getSeverityColor(hazard.severity),
                            border: `1px solid ${getSeverityColor(hazard.severity)}44`,
                          }}
                        />
                      )}
                    </Box>

                    {/* Context note - the human touch */}
                    {hazard.contextNote && (
                      <Typography
                        sx={{
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '0.8rem',
                          fontStyle: 'italic',
                          mb: 1,
                          pl: 1,
                          borderLeft: '2px solid rgba(96, 165, 250, 0.3)',
                        }}
                      >
                        "{hazard.contextNote}"
                      </Typography>
                    )}

                    {/* Regular note */}
                    {hazard.note && !hazard.contextNote && (
                      <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.8rem', mb: 1 }}>
                        {hazard.note}
                      </Typography>
                    )}

                    {/* Actions */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={confirmingId === hazard.id}
                        onClick={() => handleConfirm(hazard.id, true)}
                        startIcon={confirmingId === hazard.id ? <CircularProgress size={12} /> : <ConfirmIcon />}
                        sx={{
                          fontSize: '0.7rem',
                          py: 0.25,
                          color: '#f59e0b',
                          borderColor: 'rgba(245, 158, 11, 0.3)',
                          '&:hover': { borderColor: '#f59e0b', background: 'rgba(245, 158, 11, 0.1)' },
                        }}
                      >
                        Still there
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={confirmingId === hazard.id}
                        onClick={() => handleConfirm(hazard.id, false)}
                        startIcon={<DenyIcon />}
                        sx={{
                          fontSize: '0.7rem',
                          py: 0.25,
                          color: '#22c55e',
                          borderColor: 'rgba(34, 197, 94, 0.3)',
                          '&:hover': { borderColor: '#22c55e', background: 'rgba(34, 197, 94, 0.1)' },
                        }}
                      >
                        Gone
                      </Button>
                      
                      {onNavigateToHazard && (
                        <IconButton
                          size="small"
                          onClick={() => onNavigateToHazard(hazard.latitude, hazard.longitude)}
                          sx={{ ml: 'auto' }}
                        >
                          <NavIcon sx={{ color: '#60a5fa', fontSize: 18 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Card>
                </Fade>
              ))
            )}
          </Box>
        )}

        {/* Community Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
              {feed.basin.name} Community
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, my: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ color: '#60a5fa', fontSize: '2rem', fontWeight: 700 }}>
                  {feed.pulse.activeWorkers}
                </Typography>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem' }}>
                  Active now
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ color: '#22c55e', fontSize: '2rem', fontWeight: 700 }}>
                  {feed.pulse.safeReturnsToday}
                </Typography>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem' }}>
                  Home safe today
                </Typography>
              </Box>
            </Box>
            
            <Divider sx={{ borderColor: 'rgba(96, 165, 250, 0.1)', my: 2 }} />
            
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem', fontStyle: 'italic' }}>
              "Someone out here has my back,<br />even if I've never met them."
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BasinFeed;
